import { useState, useEffect, useCallback } from 'react';
import { User, AccessType, UserLevel } from '../types/access';
import { useAuth } from '../features/shared/context/AuthContext';

interface UseAccessControlReturn {
  user: User | null;
  userAccesses: AccessType[];
  loading: boolean;
  hasAccess: (accessType: AccessType) => boolean;
  hasAnyAccess: (accessTypes: AccessType[]) => boolean;
  hasAllAccess: (accessTypes: AccessType[]) => boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isManager: boolean;
  canManageUsers: boolean;
  canManageAccess: boolean;
  refreshUser: () => Promise<void>;
}

export const useAccessControl = (): UseAccessControlReturn => {
  const { user: authUser } = useAuth(); // Usar usuário do AuthContext
  const [user, setUser] = useState<User | null>(null);
  const [userAccesses, setUserAccesses] = useState<AccessType[]>([]);
  const [loading, setLoading] = useState(true);

  // Carregar usuário atual
  const loadCurrentUser = useCallback(async () => {
    setLoading(true);

    try {
      if (!authUser) {
        setUser(null);
        setUserAccesses([]);
        return;
      }

      const userFromAuth: User = {
        id: authUser.id,
        email: authUser.email,
        full_name: authUser.full_name,
        user_level: authUser.user_level as any,
        is_active: authUser.is_active,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      setUser(userFromAuth);
      // Permissões detalhadas são carregadas centralmente no PermissionContext.
      // Aqui mantemos apenas os dados de nível (admin/manager/super-admin)
      // para evitar requisições duplicadas ao endpoint de accesses.
      setUserAccesses([]);
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  // Verificar se tem acesso específico
  const hasAccess = useCallback((accessType: AccessType): boolean => {
    return userAccesses.includes(accessType);
  }, [userAccesses]);

  // Verificar se tem pelo menos um dos acessos
  const hasAnyAccess = useCallback((accessTypes: AccessType[]): boolean => {
    return accessTypes.some(accessType => userAccesses.includes(accessType));
  }, [userAccesses]);

  // Verificar se tem todos os acessos
  const hasAllAccess = useCallback((accessTypes: AccessType[]): boolean => {
    return accessTypes.every(accessType => userAccesses.includes(accessType));
  }, [userAccesses]);

  // Verificar se é administrador
  const isAdmin = user?.user_level === UserLevel.ADMIN || user?.user_level === UserLevel.SUPER_ADMIN;

  // Verificar se é super administrador (global)
  const isSuperAdmin = user?.user_level === UserLevel.SUPER_ADMIN;

  // Verificar se é gerente
  const isManager = user?.user_level === UserLevel.MANAGER || user?.user_level === UserLevel.ADMIN || user?.user_level === UserLevel.SUPER_ADMIN;

  // Verificar se pode gerenciar usuários
  const canManageUsers = hasAccess(AccessType.ADMIN_USERS) || isAdmin;

  // Verificar se pode gerenciar acessos
  const canManageAccess = hasAccess(AccessType.ADMIN_ROLES) || isAdmin;

  // Recarregar dados do usuário
  const refreshUser = useCallback(async () => {
    await loadCurrentUser();
  }, [loadCurrentUser]);

  // Carregar usuário na inicialização e quando authUser mudar
  useEffect(() => {
    loadCurrentUser();
  }, [loadCurrentUser, authUser]);

  return {
    user,
    userAccesses,
    loading,
    hasAccess,
    hasAnyAccess,
    hasAllAccess,
    isAdmin,
    isSuperAdmin,
    isManager,
    canManageUsers,
    canManageAccess,
    refreshUser
  };
};

export default useAccessControl;
